import React, {
  useCallback, useEffect, useReducer, useRef, useState
} from 'react';
import { CountdownTimer } from '@thd-olt-component-react/countdown-timer';
import './CpaLexisNexisQuestions.scss';
import PropTypes from 'prop-types';
import { Col, Row } from '@thd-olt-component-react/grid';
import { Button } from '@thd-olt-component-react/button';
import { Overlay } from '@thd-olt-component-react/overlay';
import { CpaRadioGroup } from '../CpaRadioGroup/CpaRadioGroup';
import { CpaSubmitButton } from '../CpaSubmitButton/CpaSubmitButton';
import { scrollToRef } from '../../constants/constants';

const CpaLexisNexisQuestions = (props) => {
  const { response, callback } = props;

  const reducer = (state, action) => {

    const newInputState = {
      ...state,
      [action.input]: {
        value: action.value,
        valid: action.valid
      }
    };

    let submitEnabled = true;
    Object.keys(newInputState)
      .forEach((key) => {
        submitEnabled = submitEnabled && newInputState[key].valid;
      });

    return {
      ...newInputState,
      submitEnabled: {
        value: submitEnabled,
        valid: true
      }
    };
  };

  const initialState = {
    submitEnabled: {
      value: false,
      valid: false
    },
    submitted: {
      value: false,
      valid: true
    }
  };

  const populateInitialStateWithQuestionIds = (initState) => {
    const init = initState;
    response.value.lnResponse.questionSet.questions.forEach(
      (ele) => {
        init[ele.questionId] = {
          value: '',
          valid: false
        };
      }
    );
    return init;
  };

  const [state, dispatch] = useReducer(reducer, populateInitialStateWithQuestionIds(initialState));

  const inputChangeHandler = useCallback(
    (value, identifier) => {
      dispatch({
        value,
        input: identifier,
        valid: true
      });
    }, [dispatch]);

  const handleSubmit = (event) => {
    dispatch({ value: true, input: 'submitted', valid: true });
    callback(event, state);
  };

  const constructOptions = (choices) => {

    return choices.map(function (value) {
      return ({
        name: value.text,
        value: value.choiceId
      });
    });

  };

  const buildQuestionsAndChoices = () => {
    return response.value.lnResponse.questionSet.questions.map(
      function (questionEle, index) {
        return (
          <CpaRadioGroup
            header={questionEle.question}
            options={constructOptions(questionEle.choices)}
            name="lexisNexisChoices"
            questionId={questionEle.questionId}
            callback={(event) => inputChangeHandler(event, questionEle.questionId)}
            key={questionEle.questionId}
          />
        );
      }
    );

  };

  const topOfFormRef = useRef();
  useEffect(() => {
    scrollToRef(topOfFormRef);
  }, []);

  const setDeadline = () => {
    const date = new Date();
    return date.setMinutes(+5 + date.getMinutes());
  };

  const handleClose = () => window.location.reload();

  const millisecondsPerDay = (1000 * 60 * 60 * 24);
  const { submitted } = state;

  const [quizTimeout, setQuizTimeout] = useState(false);
  const [timeLeft, setTimeLeft] = useState(setDeadline());

  return (
    <Row data-component="CpaLexisNexisQuestions">
      <Col xs="8">
        <div ref={topOfFormRef}>
          <Row>
            <div>
              <h2>
                LexisNexis Verification Quiz
              </h2>
              <br />
              <p>Before your request can be submitted, you must first verify your identity.
                Please select an answer to the following questions below.
              </p>
              <br />
              <p>
                <b>About the Quiz:</b> LexisNexis Risk Solutions, our third-party verification tool,
                generates the questions in the
                quiz below and validates your identity with Home Depot. Home Depot does not store or
                collect the responses to
                the questions below.
              </p>
              <br />
              <p>
                <b>Warning:</b> Do not click the Back, Refresh, or Close buttons on your browser while
                answering the questions below.
                Otherwise, your request cannot be completed and you will need to restart the process
                to submit a request.
              </p>
              <br />
              <p>
                {!quizTimeout
                && (
                  <CountdownTimer
                    classes="countdown-timer__wrapper--modifier"
                    endDate={timeLeft}
                    shouldIncludeSvg
                    shouldShowSecondsRemaining
                    shouldUseMessagingString
                    timebombThreshold={millisecondsPerDay}
                    customMessagingString="Identity Verification Quiz will expire in "
                    onRemove={() => setQuizTimeout(true)}
                  />
                )}
                <Overlay
                  onClose={handleClose}
                  open={quizTimeout && !submitted.value}
                  header="We’re sorry, your session expired…"
                  fit
                  modalClass="cpa-modal"
                >
                  <Row>
                    <Col>
                      <p>
                        We therefore will not further process your request.  If you would like to opt out
                        of the sale and sharing or use for targeted advertising of your personal information
                        or submit a new request, you can do so by visiting this page&nbsp;
                        <a
                          href="https://www.homedepot.com/privacy/Exercise-My-Privacy-Rights"
                          className="text-link"
                        >homedepot.com/privacy/Exercise-My-Privacy-Rights
                        </a>.
                      </p>
                      <br />
                      <p>
                        Please disregard the email we sent to confirm the email address you provided,
                        as the confirmation is required only if we verified your identity based on
                        the information you provided, which did not occur due to the session expiring.
                        Even if you click CONFIRM in that email, your request will not be processed.
                      </p>
                      <br />
                      <p>
                        Questions?  Email us at&nbsp;
                        <a
                          href="mailto:myinfo@homedepot.com"
                          className="text-link"
                        >
                          myinfo@homedepot.com
                        </a>.
                      </p>
                      <br />
                      <p>
                        You may learn more about our privacy practices by going to our Privacy and
                        Security Statement at&nbsp;
                        <a
                          href="https://www.homedepot.com/privacy"
                          className="text-link"
                        >www.homedepot.com/privacy
                        </a>.
                      </p>
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <Button onClick={handleClose}>Close
                      </Button>
                    </Col>
                  </Row>
                </Overlay>
              </p>
            </div>
          </Row>
        </div>
        <form onSubmit={handleSubmit} data-testid="cpa-lexisnexis-form">
          <Row>
            {buildQuestionsAndChoices()}
          </Row>
          <br />
          <Row>
            <Col xs="5">
              <div>
                <CpaSubmitButton isSubmitEnabled={state.submitEnabled.value} />
              </div>
            </Col>
          </Row>
        </form>
      </Col>
    </Row>

  );
};

CpaLexisNexisQuestions.propTypes = {
  response: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]).isRequired,
  callback: PropTypes.func.isRequired
};

CpaLexisNexisQuestions.displayName = 'CpaLexisNexisQuestions';

export { CpaLexisNexisQuestions };
